import { makeObservable, observable, action, computed, runInAction } from 'mobx';
import { RE_PLEX_ID } from '../lib/utils';
import { plainDescription, SOURCE_EDGE, SOURCE_ENTITY } from '../lib/sourceMetadata';

const BATCH_DEBOUNCE_MS = 100;
// How long a scroll intent stays "pending" so a target that mounts a beat
// late (mid-stream prose) can still honor it, without a much-later mount
// yanking the viewport unexpectedly.
const SCROLL_INTENT_TTL_MS = 4000;

class SourceReferencesStore {
  sources = [];
  highlightedSourceIds = [];  // supports multi-source highlight
  // The source last clicked (scrollToSource). It stays highlighted when the pointer moves on,
  // until another source is clicked; highlightedSourceIds is only the hover (PLEX-6500).
  selectedSourceIds = [];
  scrollToSourceId = null;
  // Bumped on every scrollToSource() call so consumers re-run even when the
  // target id is unchanged (repeated clicks on the same card).
  scrollNonce = 0;
  isDrawerOpen = false;
  urlToSourceMap = new Map(); // For O(1) deduplication
  nextSourceId = 1;
  metadataMap = new Map(); // sourceId → {loading, title, description, favicon_url, image_url, domain, error}
  // url → {title, description, kind} the app already has for a cited URL (PLEX-5920,
  // lib/sourceMetadata.js); a SourceCard with a description here skips the URL metadata fetch.
  urlDescriptionMap = new Map();
  accessInstructionsModal = null; // {href, instructions, datasetName} or null
  _pendingBatch = [];  // [{sourceId, url}]
  _batchTimer = null;
  _batchEndpoint = null;
  // The chat currently being viewed. Each source records which chat(s) it was
  // registered under (source.chatIds); SourcesPanel filters by the active
  // chat so a previous chat's sources can never render in another chat — even
  // if clearSources()'s post-render effect is missed during a navigation.
  activeChatId = null;

  constructor() {
    makeObservable(this, {
      sources: observable.deep,
      activeChatId: observable,
      setActiveChat: action,
      highlightedSourceIds: observable,
      selectedSourceIds: observable,
      scrollToSourceId: observable,
      scrollNonce: observable,
      isDrawerOpen: observable,
      accessInstructionsModal: observable,
      dedupedSources: computed,
      highlightedSourceId: computed,
      addSource: action,
      highlightSource: action,
      scrollToSource: action,
      clearHighlight: action,
      toggleDrawer: action,
      closeDrawer: action,
      clearSources: action,
      removeSourcesByAgentId: action,
      loadDrawerState: action,
      metadataMap: observable,
      urlDescriptionMap: observable,
      registerDescriptions: action,
      fetchMetadata: action,
      showAccessInstructions: action,
      hideAccessInstructions: action,
    });
  }

  // Backwards-compatible single-ID accessor
  get highlightedSourceId() {
    return this.highlightedSourceIds[0] ?? null;
  }

  get dedupedSources() {
    return this.sources;
  }

  // Set the chat currently being viewed. Sources registered after this belong
  // to that chat; the Sources panel renders only the active chat's sources.
  setActiveChat(chatId) {
    this.activeChatId = chatId ?? null;
  }

  addSource(url, title, turnId, agentId = null) {
    // Validate URL
    if (!url || url.trim() === '') {
      return null;
    }

    // Plex IDs should NOT be stored - they should be resolved to HTTP URLs first
    if (RE_PLEX_ID.test(url)) {
      // Plex IDs should be resolved to HTTP URLs before registration
      // If we see one here, it means interpolateSourceLinks hasn't run yet
      return null;
    }

    // Enhanced URL validation - check for basic URL format
    const isValidUrl = url.includes('://');

    // Check for existing source
    if (this.urlToSourceMap.has(url)) {
      const sourceId = this.urlToSourceMap.get(url);
      const existingSource = this.sources.find(s => s.id === sourceId);
      if (existingSource) {
        const isDupe = existingSource.occurrences.some(
          occ => occ.turnId === turnId && occ.agentId === agentId
        );
        if (!isDupe) existingSource.occurrences.push({turnId, agentId});
        // A URL can be cited from more than one chat — record each so the
        // panel shows it in every chat that references it (not just the first).
        if (!existingSource.chatIds) existingSource.chatIds = [];
        if (this.activeChatId != null && !existingSource.chatIds.includes(this.activeChatId)) {
          existingSource.chatIds.push(this.activeChatId);
        }
        if (title && (!existingSource.title || title.length > existingSource.title.length)) {
          existingSource.title = title;
        }
        return existingSource.id;
      }
    }

    // Create new source
    const sourceId = `source-${this.nextSourceId++}`;
    const source = {
      id: sourceId,
      url,
      title,
      turnId,
      agentId,
      chatIds: this.activeChatId != null ? [this.activeChatId] : [],
      firstSeenAt: Date.now(),
      occurrences: [{turnId, agentId}],
      isInvalid: !isValidUrl,
    };

    this.sources.push(source);
    this.urlToSourceMap.set(url, sourceId);

    return sourceId;
  }
  // Hovered or clicked: what the Sources panel and the prose show as highlighted.
  isSourceHighlighted(id) {
    return this.highlightedSourceIds.includes(id) || this.selectedSourceIds.includes(id);
  }

  highlightSource(ids) {
    this.highlightedSourceIds = Array.isArray(ids) ? ids : (ids ? [ids] : []);
  }

  scrollToSource(id) {
    this.highlightedSourceIds = id ? [id] : [];
    this.selectedSourceIds = id ? [id] : [];
    // Keep the intent "pending" (queued) instead of clearing it after one
    // tick: during streaming the clicked source's prose occurrence may not
    // be mounted yet, so an immediate clear made the scroll silently no-op
    // (PLEX-4228, markdown-factory scroll effect). A late-mounting target
    // now still sees scrollToSourceId set and honors it. The nonce re-fires
    // consumers for repeat clicks on the same id; a TTL auto-expires the
    // intent so a much-later mount doesn't grab the viewport.
    this.scrollToSourceId = id;
    this.scrollNonce += 1;
    if (this._scrollExpireTimer) clearTimeout(this._scrollExpireTimer);
    if (id) {
      this._scrollExpireTimer = setTimeout(() => {
        runInAction(() => { this.scrollToSourceId = null; this._scrollExpireTimer = null; });
      }, SCROLL_INTENT_TTL_MS);
    }
  }

  clearHighlight() {
    this.highlightedSourceIds = [];
  }

  toggleDrawer() {
    this.isDrawerOpen = !this.isDrawerOpen;

    // Persist state to localStorage
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('sourceDrawerOpen', JSON.stringify(this.isDrawerOpen));
    }
  }

  closeDrawer() {
    this.isDrawerOpen = false;

    // Persist state to localStorage
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('sourceDrawerOpen', JSON.stringify(this.isDrawerOpen));
    }
  }

  clearSources() {
    this.sources = [];
    this.urlToSourceMap.clear();
    // Clear the per-source metadata cache too. nextSourceId resets to 1 below,
    // so the next chat reuses ids (source-1, source-2, ...). Without clearing
    // metadataMap, each SourceCard would read the PREVIOUS chat's cached title
    // and structure image under the reused id — rendering another chat's
    // source (e.g. a "Sotorasib" compound image) in this chat.
    this.metadataMap.clear();
    this.highlightedSourceIds = [];
    this.selectedSourceIds = [];
    this.nextSourceId = 1;
  }

  removeSourcesByAgentId(agentId) {
    if (!agentId) return;

    // Remove occurrences that match this agentId
    this.sources.forEach(source => {
      source.occurrences = source.occurrences.filter(
        occ => occ.agentId !== agentId
      );
    });

    // Remove sources that have no remaining occurrences
    const sourcesToRemove = this.sources.filter(s => s.occurrences.length === 0);
    sourcesToRemove.forEach(source => {
      this.urlToSourceMap.delete(source.url);
    });
    this.sources = this.sources.filter(s => s.occurrences.length > 0);
  }

  getSource(id) {
    return this.sources.find(s => s.id === id) || null;
  }

  // Record the title/description already known for each URL: {url: {title, description}}, from an
  // entity (SOURCE_ENTITY) or an evidence edge (SOURCE_EDGE). Keyed by URL, not source id, so it is
  // not cleared with the sources (clearSources): a URL describes the same record in any chat.
  //  - An entry with a description beats one without: the description is what spares the card a
  //    page fetch (generic for Open Targets, a CAPTCHA for PubMed).
  //  - Otherwise an entity beats an edge: it describes its own page (a PubMed paper's abstract, not
  //    a citing edge's "Author").
  //  - The winner replaces the entry whole, even a field it lacks: a title and description from
  //    two different records (an edge's "IC50 = 90 nM" over a paper's abstract) would be one card
  //    describing two things. A card with no known title shows its link text (SourceCard).
  //  - Otherwise the first entry stays, filled in from the same kind where it lacked a title or
  //    description, so a card never flips between two edges that cite one page.
  registerDescriptions(byUrl, kind = SOURCE_EDGE) {
    const rank = (entry) => (entry.description ? 2 : 0) + (entry.kind === SOURCE_ENTITY ? 1 : 0);
    for (const [url, meta] of Object.entries(byUrl || {})) {
      const title = (typeof meta?.title === 'string' && meta.title.trim()) || null;
      const description = plainDescription(meta?.description);
      if (!url || (!title && !description)) continue;
      const existing = this.urlDescriptionMap.get(url);
      const entry = { title, description, kind };
      if (!existing || rank(entry) > rank(existing)) {
        this.urlDescriptionMap.set(url, entry);
      } else if (existing.kind === kind && ((!existing.title && title) || (!existing.description && description))) {
        this.urlDescriptionMap.set(url, {
          title: existing.title || title, description: existing.description || description, kind,
        });
      }
    }
  }

  fetchMetadata(sourceId, url, metadataEndpoint, authData = null) {
    const existing = this.metadataMap.get(sourceId);
    if (existing && (existing.loading || existing.title || existing.error)) return;

    this.metadataMap.set(sourceId, { loading: true });

    this._batchEndpoint = metadataEndpoint;
    if (authData) this._batchAuthData = authData;
    this._pendingBatch.push({ sourceId, url });

    if (this._batchTimer) clearTimeout(this._batchTimer);
    this._batchTimer = setTimeout(() => this._flushBatch(), BATCH_DEBOUNCE_MS);
  }

  _flushBatch() {
    const batch = this._pendingBatch.splice(0);
    const endpoint = this._batchEndpoint;
    const authData = this._batchAuthData;
    this._batchTimer = null;

    if (batch.length === 0) return;

    const urls = [...new Set(batch.map(b => b.url))];
    const sourcesByUrl = new Map();
    for (const { sourceId, url } of batch) {
      if (!sourcesByUrl.has(url)) sourcesByUrl.set(url, []);
      sourcesByUrl.get(url).push(sourceId);
    }

    const headers = { 'Content-Type': 'application/json' };
    const jwtToken = authData?.jwtToken || authData?.idToken?.jwtToken;
    if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`;

    fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({ urls }),
    })
      .then(resp => resp.json())
      .then(data => {
        runInAction(() => {
          const results = data.results || {};
          for (const [url, sourceIds] of sourcesByUrl) {
            const meta = results[url] || { error: true };
            for (const sid of sourceIds) {
              this.metadataMap.set(sid, { ...meta, loading: false });
            }
          }
        });
      })
      .catch(() => {
        runInAction(() => {
          for (const { sourceId } of batch) {
            this.metadataMap.set(sourceId, { loading: false, error: true });
          }
        });
      });
  }

  showAccessInstructions(href, instructions, datasetName) {
    this.accessInstructionsModal = { href, instructions, datasetName };
  }

  hideAccessInstructions() {
    this.accessInstructionsModal = null;
  }

  // Load drawer state from localStorage on init
  loadDrawerState() {
    if (typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('sourceDrawerOpen');
      if (saved !== null) {
        this.isDrawerOpen = JSON.parse(saved);
      }
    }
  }
}

export default SourceReferencesStore;